import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Compass, MessageCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { Button, EmptyState, Select, Workspace } from '@aeris/ui'

import { CustomerPanel } from '../components/customer-panel.tsx'
import { InboxChat } from '../components/inbox-chat.tsx'
import { InboxSidebar } from '../components/inbox-sidebar.tsx'
import {
  ApiError,
  assignConversationToQueue,
  fetchConversations,
  fetchInboxAccounts,
  fetchQueues,
  fetchSalespeople,
  fetchSetupProgress,
  fetchTemplates,
  openCommunicationEventStream,
  type ConversationFilterType,
  type ConversationSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  CONVERSATIONS_PAGE_SIZE,
  readPanelOpen,
  readStoredInboxAccount,
  REFRESH_INTERVAL_MS,
  resolveInboxAccount,
  SEARCH_DEBOUNCE_MS,
  STREAM_CONNECT_TIMEOUT_MS,
  useDebounced,
  writeInboxAccount,
  writePanelOpen,
} from '../lib/inbox.ts'

/**
 * Atendimento — a central de WhatsApp da loja (F046 → F085).
 *
 * ## Um ESPAÇO DE TRABALHO, não uma página
 *
 * Esta é a única tela do produto que não usa `<Page>` (ver `layout.test.ts` e
 * a doc de `Workspace` em `@aeris/ui`). Ela não é um documento que se lê de
 * cima para baixo: é uma superfície de aplicação que ocupa a área inteira
 * entre a topbar e o fim da janela, sem título, sem descrição e sem padding
 * de página.
 *
 * O motivo é medível. Numa janela de 1536px, a moldura de página consumia
 * ~920px — barra lateral 224, respiro 40, `max-w-6xl` cortando em 1152, lista
 * 304, painel 320 — e sobravam ~600 para a conversa, que é a única coisa que
 * esta tela existe para mostrar. Palavras do dono: *"ainda parece um painel
 * administrativo com um chat colocado dentro de um card"*.
 *
 * ## Onde a rolagem acontece
 *
 * Em três lugares, e em nenhum outro: a lista de conversas, a thread de
 * mensagens, e o painel de detalhes. O `<main>` do shell é `overflow-hidden`
 * para esta rota justamente para que uma thread longa não empurre a página
 * inteira — o defeito progressivo que a F081 corrigiu de outro jeito e que
 * este enquadramento fecha na raiz.
 *
 * ## A janela de 24 horas
 *
 * `lastInboundAt` é a única fonte (a Meta parou de mandar o objeto
 * `conversation` que dizia quando a janela expira, Graph API v24). A regra é
 * literalmente a mesma função que o servidor usa, `serviceWindow` de
 * `@aeris/communication` — não uma cópia com três linhas de aritmética. Uma
 * cópia diverge no dia em que uma das duas mudar, e o sintoma seria a tela
 * oferecer um envio que o servidor recusa.
 *
 * A lista de conversas é buscada UMA VEZ, aqui em cima, e não dentro do
 * componente da lista: é o que deixa a conversa aberta acompanhar um
 * `lastInboundAt` fresco quando o cliente escreve enquanto o atendente está
 * com a thread aberta.
 *
 * ## Tempo real (F047, ADR-0030), com fallback para polling
 *
 * `useCommunicationRealtime` abre um `EventSource` contra
 * `GET /communication/events` e invalida as queries certas a cada evento —
 * nunca guarda o conteúdo do evento, só usa como gatilho de "busque de novo
 * pelo caminho normal", que já autoriza e escopa por tenant. Enquanto o stream
 * está de pé, `refetchInterval` fica `false`; se ele nunca abrir (um proxy
 * bloqueando SSE) ou cair de vez, `live` vira `false` e o polling assume.
 */

/**
 * O tempo real do inbox, com fallback honesto.
 *
 * `live` começa `true` (otimista: assume que o stream vai abrir) e só vira
 * `false` quando há evidência de que não vai — o stream nunca abriu dentro de
 * `STREAM_CONNECT_TIMEOUT_MS`, ou o `EventSource` desistiu de reconectar
 * (`readyState === CLOSED`, o único jeito confiável de saber: um erro isolado é
 * o `EventSource` tentando de novo sozinho, não uma falha final).
 */
const useCommunicationRealtime = (): boolean => {
  const queryClient = useQueryClient()
  const [live, setLive] = useState(true)
  const openedRef = useRef(false)

  useEffect(() => {
    openedRef.current = false

    const connectTimeout = window.setTimeout(() => {
      if (!openedRef.current) setLive(false)
    }, STREAM_CONNECT_TIMEOUT_MS)

    const stream = openCommunicationEventStream({
      onOpen: () => {
        openedRef.current = true
        window.clearTimeout(connectTimeout)
        setLive(true)
      },
      onEvent: (event) => {
        if (event.type === 'conversation.updated' && event.id !== undefined) {
          void queryClient.invalidateQueries({
            queryKey: ['communication-conversations'],
          })
          void queryClient.invalidateQueries({
            queryKey: ['communication-messages', event.id],
          })
          return
        }
        if (
          event.type === 'message.received' &&
          event.conversationId !== undefined
        ) {
          void queryClient.invalidateQueries({
            queryKey: ['communication-messages', event.conversationId],
          })
          void queryClient.invalidateQueries({
            queryKey: ['communication-conversations'],
          })
        }
      },
      onResync: () => {
        void queryClient.invalidateQueries({
          queryKey: ['communication-conversations'],
        })
        void queryClient.invalidateQueries({
          queryKey: ['communication-messages'],
        })
      },
      onClosed: () => {
        window.clearTimeout(connectTimeout)
        setLive(false)
      },
    })

    return () => {
      window.clearTimeout(connectTimeout)
      stream.close()
    }
  }, [queryClient])

  return live
}

/**
 * O atalho para o guia (F055), enquanto houver passo pendente.
 *
 * Some sozinho quando os dez passos estão concluídos — um atalho permanente
 * para uma tarefa que termina vira entulho. Mora no RODAPÉ da coluna de
 * conversas desde a F085: no topo do espaço de trabalho ele custava uma faixa
 * de altura para toda conversa lida ali dentro, para sempre.
 */
const GuideShortcut = (): ReactNode => {
  const { allows } = useAuth()
  const canSeeGuide = allows('communication.channel.read')

  const progress = useQuery({
    queryKey: ['communication-setup-progress'],
    queryFn: async ({ signal }) => await fetchSetupProgress(signal),
    enabled: canSeeGuide,
  })

  if (!canSeeGuide) return null

  const steps = progress.data ?? []
  const pendingCount = steps.filter((step) => !step.done).length
  if (steps.length === 0 || pendingCount === 0) return null

  return (
    <Link
      to="/atendimento-guia"
      className="flex items-center gap-2 text-(length:--text-xs) font-(--weight-medium) text-(--brand-primary) hover:underline"
    >
      <Compass aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      Guia de configuração — faltam {pendingCount} de {steps.length} passos
    </Link>
  )
}

export const AtendimentoPage = (): ReactNode => {
  const live = useCommunicationRealtime()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ConversationFilterType | null>(null)
  /*
   * A escolha CRUA de número (F091) — o que está no `localStorage`, sem
   * validar. Quem valida é `resolveInboxAccount` abaixo, contra as contas que
   * existem, e o faz por DERIVAÇÃO: um efeito que corrigisse este estado ao
   * carregar as contas é o que `react-hooks/set-state-in-effect` proíbe, e
   * teria um quadro em que a tela mostra um número e o estado guarda outro.
   */
  const [chosenAccount, setChosenAccount] = useState(readStoredInboxAccount)
  const [panelOpen, setPanelOpen] = useState(readPanelOpen)
  const [queueError, setQueueError] = useState<string | null>(null)

  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)

  const togglePanel = useCallback(() => {
    setPanelOpen((open) => {
      writePanelOpen(!open)
      return !open
    })
  }, [])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  /*
   * Guardada por fora da lista, e não derivada só de `rows.find(...)`.
   *
   * A lista pagina e filtra; se a conversa aberta sair da página atual (alguém
   * respondeu outra e ela subiu, ou o atendente trocou de chip), a thread não
   * pode simplesmente sumir. `rows.find` abaixo prefere a linha mais fresca
   * quando ela ainda está visível — o que mantém `lastInboundAt` (e portanto a
   * janela de 24h) atualizado — e cai para este valor quando não está.
   */
  const [selectedFallback, setSelectedFallback] =
    useState<ConversationSummaryType | null>(null)

  /*
   * Os números da loja (F091).
   *
   * `fetchInboxAccounts`, e NÃO `fetchChannelAccounts`: aquela pede
   * `communication.channel.read`, que um caixa ou vendedor não tem — o
   * seletor apareceria para o dono e sumiria para quem trabalha. Ver a rota
   * `/communication/inbox/accounts`.
   *
   * Sem `refetchInterval`: um número novo é cadastrado uma vez por ano, não
   * uma vez por minuto.
   */
  const accountsQuery = useQuery({
    queryKey: ['communication-inbox-accounts'],
    queryFn: async ({ signal }) => await fetchInboxAccounts(signal),
  })

  const accounts = accountsQuery.data ?? []
  const accountId = resolveInboxAccount(chosenAccount, accountsQuery.data)

  const conversationsQuery = useQuery({
    queryKey: [
      'communication-conversations',
      { page, search: debouncedSearch, filter, accountId },
    ],
    queryFn: async ({ signal }) =>
      await fetchConversations(
        {
          limit: CONVERSATIONS_PAGE_SIZE,
          offset: (page - 1) * CONVERSATIONS_PAGE_SIZE,
          search: debouncedSearch,
          ...(filter === null ? {} : { filter }),
          ...(accountId === null ? {} : { accountId }),
        },
        signal
      ),
    // Mantém a página anterior na tela enquanto a próxima carrega, em vez de
    // colapsar para um spinner a cada troca de página ou de recorte.
    placeholderData: (previous) => previous,
    refetchInterval: live ? false : REFRESH_INTERVAL_MS,
  })

  /*
   * Quantas não lidas existem AO TODO — o número no chip.
   *
   * Uma consulta própria, com `limit: 1`, porque a resposta que interessa é o
   * `total`, não as linhas. Derivar isso da página visível diria "3" quando há
   * doze, que é pior do que não dizer nada.
   */
  const unreadQuery = useQuery({
    /*
     * O número escolhido entra na chave E na consulta (F091). Sem isso o chip
     * diria "Não lidas 3" com a lista filtrada mostrando uma — a mentira
     * pequena que esta feature existe para não criar.
     */
    queryKey: ['communication-conversations', 'unread-total', { accountId }],
    queryFn: async ({ signal }) =>
      await fetchConversations(
        {
          limit: 1,
          offset: 0,
          filter: 'unread',
          ...(accountId === null ? {} : { accountId }),
        },
        signal
      ),
    refetchInterval: live ? false : REFRESH_INTERVAL_MS,
  })

  const queuesQuery = useQuery({
    queryKey: ['communication-queues'],
    queryFn: async ({ signal }) => await fetchQueues(signal),
  })

  // Os modelos aprovados (F050) — buscados aqui, uma vez, e filtrados por
  // conta dentro do chat.
  const templatesQuery = useQuery({
    queryKey: ['communication-templates'],
    queryFn: async ({ signal }) => await fetchTemplates(undefined, signal),
  })

  const salespeopleQuery = useQuery({
    queryKey: ['team-salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
  })

  const rows = conversationsQuery.data?.conversations ?? []
  const total = conversationsQuery.data?.total ?? 0
  const queues = queuesQuery.data ?? []
  const templates = templatesQuery.data ?? []
  const employeeNames = new Map(
    (salespeopleQuery.data?.items ?? []).map((person) => [
      person.userId,
      person.displayName,
    ])
  )

  const selected =
    rows.find((conversation) => conversation.id === selectedId) ??
    selectedFallback

  const selectConversation = (conversation: ConversationSummaryType): void => {
    setSelectedId(conversation.id)
    setSelectedFallback(conversation)
  }

  const changeQueue = async (queueId: string | null): Promise<void> => {
    if (selected === null) return
    try {
      await assignConversationToQueue(selected.id, { queueId })
      setQueueError(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-conversations'],
      })
    } catch (cause) {
      setQueueError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mover esta conversa de fila.'
      )
    }
  }

  const { allows } = useAuth()
  const canAssign = allows('communication.conversation.assign')

  return (
    /*
     * `flex-row` e não `grid`: as três regiões têm larguras FIXAS nas pontas e
     * elástica no meio, que é exatamente o que `shrink-0` + `flex-1` diz — e
     * sem a armadilha da F081, onde uma grade de altura limitada precisa
     * declarar `grid-rows-[minmax(0,1fr)]` ou o `h-full` das colunas cai
     * silenciosamente para `auto`.
     *
     * Abaixo de `lg` as regiões empilham: forçar três colunas num tablet
     * espreme as três até nenhuma servir.
     */
    <Workspace className="lg:flex-row">
      <InboxSidebar
        rows={rows}
        total={total}
        isPending={conversationsQuery.isPending}
        isError={conversationsQuery.isError}
        onRetry={() => {
          void conversationsQuery.refetch()
        }}
        page={page}
        onPageChange={setPage}
        pageSize={CONVERSATIONS_PAGE_SIZE}
        selectedId={selectedId}
        onSelect={selectConversation}
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        filter={filter}
        onFilterChange={(value) => {
          setFilter(value)
          setPage(1)
        }}
        accounts={accounts}
        accountId={accountId}
        onAccountChange={(value) => {
          setChosenAccount(value)
          writeInboxAccount(value)
          // Página 1: a página 3 de "todos" quase nunca existe num número só,
          // e uma lista vazia por paginação parece uma lista vazia por filtro.
          setPage(1)
        }}
        unreadTotal={unreadQuery.data?.total ?? 0}
        footer={<GuideShortcut />}
        hiddenOnSmall={selected !== null}
      />

      {selected === null ? (
        <section
          aria-label="Conversa"
          className="hidden min-h-0 min-w-0 flex-1 items-center justify-center bg-(--surface) lg:flex"
        >
          <EmptyState
            icon={<MessageCircle aria-hidden="true" className="h-8 w-8" />}
            title="Selecione uma conversa"
            description="Escolha uma conversa na lista à esquerda para ler e responder."
          />
        </section>
      ) : (
        // Chaveada pelo id: trocar de conversa reseta o rascunho e o erro de
        // envio em vez de carregá-los junto com a mensagem errada.
        <InboxChat
          key={selected.id}
          conversation={selected}
          onBack={() => {
            setSelectedId(null)
            setSelectedFallback(null)
          }}
          hiddenOnSmall={panelOpen}
          live={live}
          employeeNames={employeeNames}
          templates={templates}
          panelOpen={panelOpen}
          onTogglePanel={togglePanel}
          showChannel={accounts.length > 1 && accountId === null}
        />
      )}

      {/*
       * Fechado, o painel sai do DOM em vez de ficar escondido: um `hidden`
       * deixaria os botões dele alcançáveis por Tab e lidos por leitor de tela,
       * o que é pior que não existir — a pessoa navegaria para controles que
       * não estão na tela.
       */}
      {!panelOpen || selected === null ? null : (
        <aside
          id="inbox-side-panel"
          aria-label="Sobre esta conversa"
          className="flex min-h-0 w-full flex-1 flex-col gap-3 overflow-y-auto border-(--border-default) bg-(--surface) p-3 lg:w-80 lg:flex-none lg:border-l"
        >
          {/*
           * Abaixo de `lg` este painel OCUPA a tela — ele substitui a conversa,
           * cujo cabeçalho (e portanto o botão que o fecha) fica escondido.
           * Sem esta saída, abrir os detalhes num tablet seria um beco: o
           * painel na frente e nenhum caminho de volta.
           */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start lg:hidden"
            leadingIcon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
            onClick={togglePanel}
          >
            Voltar para a conversa
          </Button>

          {/* Permissão só esconde o controle — a API decide de verdade. */}
          {canAssign ? (
            <label className="flex flex-col gap-1">
              <span className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                Fila
              </span>
              <Select
                aria-label="Fila da conversa"
                value={selected.queueId ?? ''}
                onChange={(event) => {
                  void changeQueue(
                    event.target.value === '' ? null : event.target.value
                  )
                }}
              >
                <option value="">Sem fila</option>
                {queues.map((queue) => (
                  <option key={queue.id} value={queue.id}>
                    {queue.name}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}

          {queueError === null ? null : (
            <p role="alert" className="text-(length:--text-xs) text-(--danger)">
              {queueError}
            </p>
          )}

          {/*
            F062 — quem é este número. A camada da F048 existia inteira no
            servidor e não tinha consumidor nenhum aqui: o atendente via um
            telefone e nada mais.
          */}
          <CustomerPanel
            conversationId={selected.id}
            contactId={selected.contactId}
          />
        </aside>
      )}
    </Workspace>
  )
}
